/**
 * src/components/public/MapsLazyEmbed.tsx
 *
 * Google Maps embed lazy reveal — iframe DOM'a SADECE kullanıcı "Haritayı göster"
 * butonuna bastığında girer. KVKK + Best Practices kazanımı:
 *
 * Onceki: iframe her zaman DOM'da → Google 3rd party cookies set → Lighthouse
 * BP 77/100, KVKK 3rd-party tracking risk.
 *
 * Yeni: default placeholder + click-to-reveal → kullanıcı isterse Maps yüklenir
 *       (consent equivalent), 3rd party cookies yalnız o noktadan sonra.
 *
 * Kullanıcı tercihi sessionStorage'da kaydedilir — aynı sayfa içinde tekrar
 * onaylama yok. Yeni session'da tekrar onaylar (KVKK uyumu).
 */
'use client'

import { useEffect, useState } from 'react'

interface MapsLazyEmbedProps {
  src: string
  title: string
  /** Placeholder altında gözüken çağrı metni. */
  consentLabel: string
  /** Onay sonrası tekrar göstermeme bilgilendirme. */
  consentNote: string
  /** Buton metni. */
  loadButtonLabel: string
}

const SESSION_KEY = 'maps-consent-given'

export function MapsLazyEmbed({
  src,
  title,
  consentLabel,
  consentNote,
  loadButtonLabel,
}: MapsLazyEmbedProps) {
  const [revealed, setRevealed] = useState(false)
  const [mounted, setMounted] = useState(false)

  // SessionStorage check (sadece client'ta) — hydration safe
  useEffect(() => {
    setMounted(true)
    if (typeof window === 'undefined') return
    try {
      if (window.sessionStorage.getItem(SESSION_KEY) === '1') {
        setRevealed(true)
      }
    } catch {
      // sessionStorage erişimi (private mode vb.) — varsayılan davranış
    }
  }, [])

  if (revealed) {
    return (
      <iframe
        src={src}
        title={title}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        allowFullScreen
        className="absolute inset-0 w-full h-full border-0"
      />
    )
  }

  // SSR + ilk client render: placeholder (hydration uyumu için aynı içerik).
  // mounted=true sonrası sessionStorage'tan onay alındıysa revealed=true olur.
  const handleLoad = () => {
    setRevealed(true)
    try {
      window.sessionStorage.setItem(SESSION_KEY, '1')
    } catch {
      // sessizce atla
    }
  }

  return (
    <div className="absolute inset-0 w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 p-6 text-center">
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        className="w-12 h-12 mb-4 text-brand-600 dark:text-brand-400"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
      </svg>
      <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
        {consentLabel}
      </p>
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-4 max-w-md">
        {consentNote}
      </p>
      <button
        type="button"
        onClick={handleLoad}
        disabled={!mounted}
        className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 dark:bg-brand-600 dark:hover:bg-brand-500 text-white font-semibold text-sm px-5 py-2.5 transition-colors disabled:opacity-50"
      >
        {loadButtonLabel}
      </button>
    </div>
  )
}
